<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'
import SearchInput from '@/components/shared/SearchInput.vue'

const route = useRoute()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()

const props = defineProps({
  icon: {
    type: Boolean,
    default: false
  },
  dataSource: {
    type: Object,
    default: null
  },
  hideSeparator: {
    type: Boolean,
    default: false
  },
  hideArchived: {
    type: Boolean,
    default: false
  },
  kpiEnabled: {
    type: Boolean,
    default: false
  },
  kpiVisible: {
    type: Boolean,
    default: false
  },
  filterKey: {
    type: String,
    default: null
  },
  iconColor: {
    type: String,
    default: 'white'
  }
})

const emit = defineEmits(['toggle-kpi'])

const filterKey = computed(() => props.filterKey ?? route.name)

const sortMode = computed(() => {
  return webSiteStore.filterSortFields[filterKey.value]?.sortMode ?? false
})

const sortFields = computed(() => {
  return webSiteStore.filterSortFields[filterKey.value]?.sortFields ?? []
})

const filterValue = computed(() => {
  return webSiteStore.filterSortFields[filterKey.value]?.filterValue ?? null
})

const showArchived = ref(false)
const onArchivedClick = async() => {
  showArchived.value = !showArchived.value
  await updateDataSourceAsync()
}

const dataSource = props.dataSource

const onIconClick = () => {
  webSiteStore.isFilterBarVisible = !webSiteStore.isFilterBarVisible
}

const isIconVisible = computed(() => {
  return props.icon && sortFields.value.length > 0
})

const onSortClick = async(currentField) => {
  if (currentField.sort === null) {
    currentField.sort = 'asc'
    currentField.order = sortFields.value.filter(x => x.sort !== null).length
  } else if (currentField.sort === 'asc') {
    currentField.sort = 'desc'
    currentField.order = sortFields.value.filter(x => x.sort !== null).length
  } else {
    currentField.sort = null
    currentField.order = null
  }

  webSiteStore.filterSortFields[filterKey.value].sortFields = sortFields.value
    .map(field => {
      if (field.sort !== null) {
        field.order = sortFields.value.filter(x => x.sort !== null).indexOf(field)
      }
      return field
    })

  await updateDataSourceAsync()
}

const updateDataSourceAsync = async() => {
  const orderBy = webSiteStore.buildSortOrderBy(filterKey.value)

  if (dataSource) {
    const filter = []
    if (showArchived.value) {
      filter.push(['archived', '=', true])
    }
    if (filterValue.value) {
      if (filter.length > 0) {
        filter.push('and')
      }
      filter.push(['search_field', 'contains', filterValue.value.toLowerCase()])
    }
    dataSource.filter(filter.length > 0 ? filter : null)
    dataSource.sort(orderBy)
    dataSource.reload()
  }
}

const onInputChanged = async (value) => {
  webSiteStore.filterSortFields[filterKey.value].filterValue = value
  await updateDataSourceAsync()
}

const iconOpacity = ref(1)
const onSortModeClick = () => {
  iconOpacity.value = 0
  setTimeout(() => {
    iconOpacity.value = 1
    webSiteStore.filterSortFields[filterKey.value].sortMode = !webSiteStore.filterSortFields[filterKey.value].sortMode
  }, 100)
}

</script>
<template>
  <div v-if="sortFields.length > 0">
    <q-btn
      v-if="isIconVisible && isMobile"
      flat
      round
      dense
      icon="mdi-filter-variant"
      class="q-mr-sm q-ml-sm"
      :color="filterValue ? 'orange' : props.iconColor"
      @click="onIconClick"
    />
    <div
      v-else-if="!props.icon"
      class="q-px-sm q-mx-sm"
      :style="{
        overflow: 'hidden',
        maxHeight: webSiteStore.isFilterBarVisible ? '500px' : '0px',
        transition: 'max-height 0.5s',
      }"
    >
      <div
        class="flex items-center flex-row q-my-md"
        :style="{
          opacity: iconOpacity,
          transition: 'opacity 0.3s, transform 0.3s',
        }"
      >
        <div
          class="flex"
          style="flex: 1"
        >
          <div
            v-if="sortMode"
            style="margin-top: 0.5rem; padding-bottom: 0.2rem;"
          >
            <q-chip
              v-for="field in sortFields"
              :key="field.field"
              clickable
              dense
              :style="{
                opacity: field.sort !== null ? 1 : 0.5,
                transition: 'opacity 0.3s'
              }"
              @click="onSortClick(field)"
            >
              <q-icon
                name="mdi-arrow-up"
                class="q-mr-xs"
                :style="{
                  transition: 'transform 0.3s',
                  transform: field?.sort === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                }"
              />
              {{ $t(field.caption) }}
              <span v-if="sortFields.filter(x => x.sort != null).length > 1 && field.sort !== null">
                &nbsp;({{ field.order + 1 }})
              </span>
            </q-chip>
          </div>
          <div
            v-else
            class="flex items-center no-wrap"
            style="flex: 1"
          >
            <slot name="filter" />
            <search-input
              :model-value="filterValue"
              class="col q-mr-md"
              @update:model-value="onInputChanged($event)"
            />
          </div>
        </div>
        <q-icon
          :name="sortMode ? 'mdi-sort' : 'mdi-filter-variant'"
          size="sm"
          class="q-mr-md"
          :style="{
            opacity: iconOpacity,
            transition: 'opacity 0.1s, transform 0.2s',
            transform: `rotateY(${sortMode ? 0 : 180}deg)`,
          }"
          @click="onSortModeClick"
        />
        <q-icon
          v-if="!props.hideArchived"
          :name="showArchived ? 'mdi-archive' : 'mdi-archive-outline'"
          size="sm"
          :style="{
            opacity: showArchived ? 1 : 0.5,
            transition: 'opacity 0.1',
          }"
          @click="onArchivedClick"
        />
        <q-icon
          v-if="props.kpiEnabled"
          :name="props.kpiVisible ? 'mdi-chart-box' : 'mdi-chart-box-outline'"
          size="sm"
          class="q-ml-md"
          :style="{
            opacity: props.kpiVisible ? 1 : 0.5,
            transition: 'opacity 0.1',
          }"
          @click="emit('toggle-kpi')"
        />
      </div>
      <div v-if="$slots.filterSecondRow" class="q-mb-md">
        <slot name="filterSecondRow" />
      </div>
      <q-separator v-if="!props.hideSeparator" />
    </div>
  </div>
</template>
